import React from "react";
import { Alert, Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import EventSeatRoundedIcon from "@mui/icons-material/EventSeatRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import TableRestaurantRoundedIcon from "@mui/icons-material/TableRestaurantRounded";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import {
  hasProjectPermission,
  hasProjectRole,
  normalizeProjectAccess,
} from "../../utils/projectAccess";
import { isSuperAdmin } from "../../utils/roles";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import RestaurantTableLayoutEditor from "../../components/dashboard/tables/RestaurantTableLayoutEditor";
import RestaurantTableOperationsView from "../../components/dashboard/tables/RestaurantTableOperationsView";
import TableReservationsPanel from "../../components/dashboard/reservations/TableReservationsPanel";

const BRAND = { dark: "#19242F" };
const RESERVATION_READ_ROLES = ["owner", "admin", "vendedor", "viewer", "facturacion"];
const RESERVATION_MANAGE_ROLES = ["owner", "admin", "vendedor"];

function capabilityEnabled(project, capability, legacyModule) {
  return Boolean(project && (
    project?.capabilities?.[capability] === true ||
    project?.modules?.[legacyModule] === true ||
    isProjectModuleEnabled(project, legacyModule)
  ));
}

export default function ProjectTablesLayoutPage() {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [tab, setTab] = React.useState("operation");

  React.useEffect(() => { loadProjects(); }, [loadProjects]);
  const project = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId || item._id === projectId) || activeProject,
    [projects, projectId, activeProject],
  );
  React.useEffect(() => {
    const found = projects.find((item) => item.slug === projectId || item.id === projectId || item._id === projectId);
    if (found && found !== activeProject) setActiveProject(found);
  }, [projectId, projects, activeProject, setActiveProject]);

  const access = React.useMemo(() => normalizeProjectAccess(project?.memberAccess), [project?.memberAccess]);
  const superAdmin = isSuperAdmin(user);
  const accessResolved = superAdmin || access.resolved;
  const tablesEnabled = capabilityEnabled(project, "tables", "tables");
  const reservationsEnabled = capabilityEnabled(project, "tableReservations", "table_reservations");
  const canReadTables = tablesEnabled && (superAdmin || !accessResolved || hasProjectPermission(access, "tables.read"));
  const canManageTables = tablesEnabled && (superAdmin || (accessResolved && hasProjectPermission(access, "tables.manage")));
  const canReadReservations = reservationsEnabled && (
    superAdmin || !accessResolved || hasProjectRole(access, RESERVATION_READ_ROLES)
  );
  const canManageReservations = reservationsEnabled && (
    superAdmin || (accessResolved && hasProjectRole(access, RESERVATION_MANAGE_ROLES))
  );
  const operationsEnabled = canReadTables && canReadReservations;

  React.useEffect(() => {
    const requestedView = String(searchParams.get("vista") || "").trim().toLowerCase();
    const action = String(searchParams.get("accion") || "").trim().toLowerCase();

    if (action === "crear-mesa" && canManageTables) {
      setTab("layout");
      return;
    }
    if (requestedView === "reservas" && canReadReservations) {
      setTab("reservations");
      return;
    }
    if (requestedView === "plano" && canReadTables) {
      setTab("layout");
      return;
    }
    if (requestedView === "mesas" && canReadTables) {
      setTab(operationsEnabled ? "operation" : "layout");
      return;
    }
    if (operationsEnabled) {
      setTab("operation");
      return;
    }
    if (canReadTables) setTab("layout");
    else if (canReadReservations) setTab("reservations");
  }, [canReadTables, canManageTables, canReadReservations, operationsEnabled, searchParams]);

  const handleTabChange = (_event, value) => {
    setTab(value);
    const next = new URLSearchParams(searchParams);
    next.delete("accion");
    if (value === "reservations") next.set("vista", "reservas");
    else if (value === "layout") next.set("vista", "plano");
    else next.set("vista", "mesas");
    setSearchParams(next, { replace: true });
  };

  if (!project) return <DashboardLoadingState label="Preparando mesas y reservas…" minHeight={360} />;
  if (!tablesEnabled && !reservationsEnabled) {
    return <ModuleDisabledState projectId={projectId} title="Mesas" description="Activá Mesas o Reservas de mesa para usar la operación gastronómica." />;
  }
  if (accessResolved && !canReadTables && !canReadReservations) {
    return (
      <Alert severity="warning">
        Tu acceso al proyecto no incluye mesas ni reservas gastronómicas. Pedile al titular o a un administrador que revise tu rol y permisos.
      </Alert>
    );
  }

  const visibleTabs = [
    operationsEnabled && ["operation", <GridViewRoundedIcon />, "Salón"],
    canReadTables && ["layout", <TableRestaurantRoundedIcon />, canManageTables ? "Plano" : "Plano"],
    canReadReservations && ["reservations", <EventSeatRoundedIcon />, "Reservas"],
  ].filter(Boolean);
  const pageTitle = operationsEnabled
    ? "Mesas y reservas"
    : canReadReservations && !canReadTables
      ? "Reservas de mesa"
      : "Mesas";
  const pageDescription = operationsEnabled
    ? "Salón, plano y reservas en un mismo circuito."
    : canReadReservations && !canReadTables
      ? "Reservas gastronómicas."
      : "Plano y operación de mesas.";

  return (
    <Stack spacing={{ xs: 2, md: 3 }} sx={{ minWidth: 0 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark, fontSize: { xs: 30, sm: 36, md: 44 }, lineHeight: 1.08 }}>{pageTitle}</Typography>
        <Typography sx={{ mt: .5, color: "rgba(25,36,47,.65)", fontSize: { xs: 14, sm: 16 } }}>{project?.name || projectId} · {pageDescription}</Typography>
      </Box>

      {visibleTabs.length > 1 ? (
        <Box sx={{ mx: { xs: -1, sm: 0 }, px: { xs: 1, sm: 0 }, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons={false}
            allowScrollButtonsMobile
            sx={{
              minHeight: 48,
              "& .MuiTab-root": {
                minHeight: 48,
                minWidth: { xs: 104, sm: 132 },
                px: { xs: 1.25, sm: 2 },
                fontWeight: 900,
                textTransform: "none",
              },
            }}
          >
            {visibleTabs.map(([value, icon, label]) => <Tab key={value} value={value} icon={icon} iconPosition="start" label={label} />)}
          </Tabs>
        </Box>
      ) : null}

      {operationsEnabled && tab === "operation" ? <RestaurantTableOperationsView projectId={projectId} /> : null}
      {canReadTables && tab === "layout" ? <RestaurantTableLayoutEditor projectId={projectId} canManage={canManageTables} /> : null}
      {canReadReservations && tab === "reservations" ? <TableReservationsPanel projectId={projectId} canManage={canManageReservations} /> : null}
    </Stack>
  );
}
